import { Dialog, DialogActions, DialogContent, DialogTitle, Grid, Input, TextField, Typography } from "@mui/material";
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Button } from "@mui/material";
import { useState } from "react";
import { useMsal } from "@azure/msal-react";
import { fetchDocumentUrlAndOpenDialog } from "../../services/sharepoint.service";
import { formatDate } from "../../utils/utilFunctions";
import { Add } from "@mui/icons-material";
import { CVDataType, CVFileBlobType } from "../../types/dataTypes";
import { useNotification } from "../Shared/NotificationProvider";

type CVTableProps = {
  headers: string[],
  accessToken: string,
  setCVData: any,
  setCvFileBlob: Function,
  cvData: CVDataType[],
  cvFileBlob: CVFileBlobType[],
  isEditMode: boolean,
  deletedCvIDs: number[],
  setDeletedCvIDs: React.Dispatch<React.SetStateAction<number[]>>,
}

function CVTable({ headers, accessToken, setCVData, setCvFileBlob, cvData, cvFileBlob, isEditMode, deletedCvIDs, setDeletedCvIDs}: CVTableProps) {
  const [openAddCVDialog, setOpenAddCVDialog] = useState(false);
  const [cvLanguage, setCvLanguage] = useState("");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const { accounts } = useMsal();
  const username = accounts[0]?.username;
  const notify = useNotification();

  const handleDelete = (cvToDelete: CVDataType) => {
    try {
      if (!Array.isArray(cvData)) {
        console.error('cvData is not an array');
        return;
      }

      setCVData(cvData.filter((cv: CVDataType) => cv !== cvToDelete));
      if (cvToDelete.cvID !== undefined && !deletedCvIDs.includes(cvToDelete.cvID)) {
        setDeletedCvIDs((ids) => [...ids, cvToDelete.cvID as number]);
      }
      if (cvToDelete.isNew) {
        setCvFileBlob(cvFileBlob.filter((file) => file.fileName !== cvToDelete.cvFile));
      }
      notify('The file has been marked for deletion. Please save the changes to confirm.', 'info');
  
    } catch (error) {
      console.error('Error deleting document', error);
    }
  };

  const handleOpenAddCVDialog = () => {
    setOpenAddCVDialog(true);
  };

  const handleCloseAddCVDialog = () => {
    setOpenAddCVDialog(false);
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) {
      const file = event.target.files[0];
      const fileType = file.name.split('.').pop()?.toLowerCase();

      if (fileType === 'pdf' || fileType === 'doc' || fileType === 'docx' || fileType === 'odt') {
        setSelectedFile(file);
      } else {
        notify('Invalid file type. Please select a PDF, DOC, DOCX, or ODT file.', 'warning');
      }
    }
  };

  const handleUpload = async () => {
    if (!cvLanguage) {
      notify('Please enter a language or Variant Name for the CV.', 'warning');
      return;
    }
    if (selectedFile) {
      // Check if the file size is greater than 10MB
      const fileSizeInMB = selectedFile.size / (1024 * 1024);
      if (fileSizeInMB > 10) {
        notify('The file size exceeds 10MB. Please upload a smaller file.', 'warning');
        return;
      }
  
      // Check if a file with the same name is already uploaded, if yes, do not upload
      if (Object.keys(cvData).length > 0) {
        const isDuplicate = cvData.some((row) => {
          if (row.cvFile === selectedFile.name) {
            notify('A file with the same name is already uploaded. Please rename the file and try again.', 'warning');
            return true;
          }
          return false;
        });
  
        if (isDuplicate) {
          return;
        }
      }
  
      handleCloseAddCVDialog();
  
      // Current date
      const currentDate = new Date();
  
      // Get the file type
      const fileType = selectedFile.name.split('.').pop()?.toLowerCase();
  
      const addedCVDataItem: CVDataType = {
        cvFile: selectedFile.name,
        cvLanguage: cvLanguage,
        cvDate: currentDate.toLocaleDateString('en-CA'),
        cvUploadedBy: username,
        cvDataType: fileType,
        viewCV: '',
        isNew: true
      };
  
      if (Object.keys(cvData).length === 0) {
        setCVData([addedCVDataItem]);
        setCvFileBlob([{blob: selectedFile, fileName: selectedFile.name}]);
        return;
      }
      // Add the new row to the data state when data has already some rows
      if (Object.keys(cvData).length > 0) {
        setCVData([...cvData, addedCVDataItem]);
        setCvFileBlob([...cvFileBlob, {blob: selectedFile, fileName: selectedFile.name}]);
      }
      notify('The file has been added. Please save the changes to confirm.', 'info');
    }
  };

  return (
    <Grid>
      {cvData.length > 0 ? (
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                {headers.map((header, index) => (
                  <TableCell key={index}>
                    <Typography variant="subtitle2">{header}</Typography>
                  </TableCell>
                ))}
                <TableCell>
                  <Typography variant="subtitle2">Delete</Typography>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {cvData.length > 0 ? (
                cvData.map((row, index) => (
                  <TableRow key={index}>
                    {headers.map((header) => (
                      <TableCell key={header}>
                        {header !== 'View CV' ? (
                          header === 'cvDate' ? (row.cvDate ? formatDate(row.cvDate) : '') :
                            header === 'viewCV' ? (
                              row.viewCV !== undefined ? (
                                <Button color="primary" onClick={() => fetchDocumentUrlAndOpenDialog(row.viewCV, accessToken)}>
                                  View CV
                                </Button>) : null
                            ) : row[header as keyof CVDataType]
                        ) : (
                          row.viewCV !== undefined ? (
                            <Button color="primary" onClick={() => fetchDocumentUrlAndOpenDialog(row.viewCV, accessToken)}>
                              View CV
                            </Button>) : null
                        )}
                      </TableCell>
                    ))}
                    <TableCell>
                      <Button
                        variant="contained"
                        color="error"
                        disabled={!isEditMode}
                        onClick={() => {
                          const isConfirmed = window.confirm("Are you sure you want to delete this file?");
                          if (isConfirmed) {
                            handleDelete(row);
                          }
                        }}
                      >
                        Delete
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={headers.length + 1}>No data available</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Grid container justifyContent="left" paddingTop={2}>
          <Button disabled={!isEditMode} startIcon={<Add />} variant="outlined" color="primary" onClick={handleOpenAddCVDialog}>
            Add CV
          </Button>
        </Grid>
      )}
      {cvData.length > 0 ? (
        <Grid container justifyContent="left" paddingTop={2}>

          <Button disabled={!isEditMode} startIcon={<Add />} variant="outlined" color="primary" onClick={handleOpenAddCVDialog}>
            Add another CV
          </Button>
        </Grid>
      ) : null}
      <Dialog open={openAddCVDialog} onClose={handleCloseAddCVDialog}>
        <DialogTitle>Add New CV</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="CV Language or Variant Name"
            type="text"
            fullWidth
            value={cvLanguage}
            onChange={(event) => setCvLanguage(event.target.value)}
          />
          <Input
            type="file"
            onChange={handleFileChange}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAddCVDialog} color="primary">
            Cancel
          </Button>
          <Button onClick={handleUpload} color="primary">
            Upload
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  )
}

export default CVTable;